import type { ViewContainer, ViewContext, ViewLayout } from '@crowdex/plugin-contracts';
import { InlineMessage } from '@crowdex/ui/components/feedback';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHostTheme } from '@/features/plugins/plugin-theme';
import { type AppBridgeHandlers, hostAppBridge } from './app-bridge-host';
import { openAppView } from './apps-client';

export type AppFrameProps = {
  plugin: string;
  path: string;
  container: ViewContainer;
  /** Where the host shows it (`context.layout`): full screen, docked, a dialog, a card. */
  layout?: ViewLayout | undefined;
  /** The view's route (`context.route`, `page.navigate`). */
  route?: string | undefined;
  /** Who made what it shows (`context.origin`). */
  origin?: ViewContext['origin'] | undefined;
  /** The route open in its inspector (`context.inspector`); undefined where there is none. */
  inspector?: string | null | undefined;
  /** The last click on an action of the host's bar (`context.action`). */
  action?: ViewContext['action'] | undefined;
  /**
   * The conversation whose workspace the view works in (its files, commands, browser): an
   * opaque handle the view's requests carry, never shown to the plugin.
   */
  workspace?: string | undefined;
  item?: string | null | undefined;
  /** The sidebar section (`page.sidebars` id) `item` is of. */
  section?: string | null | undefined;
  /** What the user picked last in the view's live sections (`page.sections.set`). */
  selection?: ViewContext['selection'] | undefined;
  /** The view's route in its URL fragment (`#/runs/<id>`), for views routing on it. */
  fragment?: string | undefined;
  artifact?: ViewContext['artifact'] | undefined;
  /** What opened the view handed it. */
  input?: unknown;
  /** A call view: where the voice call stands. */
  call?: ViewContext['call'] | undefined;
  /** `media.camera` / `media.microphone` the plugin declared. */
  media?: readonly ('camera' | 'microphone')[] | undefined;
  handlers: Omit<AppBridgeHandlers, 'context'>;
  title: string;
};

/**
 * A view of an app: a frame on the plugin's own origin, served by this environment, talking
 * to the page through the bridge (`window.crowdex`).
 */
export function AppFrame(props: AppFrameProps) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [view, setView] = useState<{ url: string; origin: string; workspace: boolean } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  // The theme where the view is mounted: the desktop's (`body[data-theme]`), not the root's.
  const [host, setHost] = useState<Element | null>(null);
  const theme = useHostTheme(useMemo(() => ({ current: host }), [host]));
  const frameRef = useCallback((element: HTMLIFrameElement | null) => {
    frame.current = element;
    setHost(element);
  }, []);
  const conversationId = props.workspace;
  const artifactKey = props.artifact ? `${props.artifact.space}:${props.artifact.path}` : '';

  // A new view (and token) when what it is opened for changes.
  useEffect(() => {
    let live = true;
    setView(null);
    setError(null);
    const [space, ...path] = artifactKey.split(':');
    openAppView({
      plugin: props.plugin,
      path: props.path,
      container: props.container,
      ...(conversationId === undefined ? {} : { conversation: conversationId }),
      ...(props.item ? { item: props.item } : {}),
      ...(artifactKey && space ? { artifact: { space, path: path.join(':') } } : {}),
    }).then(
      (opened) => {
        if (live) setView(opened);
      },
      (reason: unknown) => {
        if (live) setError(reason instanceof Error ? reason.message : String(reason));
      },
    );
    return () => {
      live = false;
    };
  }, [props.plugin, props.path, props.container, conversationId, props.item, artifactKey]);

  const latest = useRef(props);
  latest.current = props;
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const bridge = useRef<ReturnType<typeof hostAppBridge> | null>(null);

  useEffect(() => {
    const element = frame.current;
    if (view === null || element === null) return;
    const context = (): ViewContext => {
      const current = latest.current;
      return {
        plugin: current.plugin,
        container: current.container,
        theme: {
          mode: themeRef.current.colorScheme,
          variables: { ...themeRef.current.tokens },
          name: themeRef.current.dataTheme,
        },
        // What the environment opened the view with: a conversation not saved has no workspace.
        workspace: view.workspace,
        ...(current.layout === undefined ? {} : { layout: current.layout }),
        ...(current.route === undefined ? {} : { route: current.route }),
        ...(current.origin === undefined ? {} : { origin: current.origin }),
        ...(current.action === undefined ? {} : { action: current.action }),
        ...(current.inspector === undefined ? {} : { inspector: current.inspector }),
        ...(current.item !== undefined ? { item: current.item } : {}),
        ...(current.section !== undefined ? { section: current.section } : {}),
        ...(current.selection !== undefined ? { selection: current.selection } : {}),
        ...(current.artifact ? { artifact: current.artifact } : {}),
        ...(current.input === undefined ? {} : { input: current.input }),
        ...(current.call === undefined ? {} : { call: current.call }),
      };
    };
    const host = hostAppBridge(element, view.origin, {
      context,
      ask: (text) => latest.current.handlers.ask(text),
      attach: (selections, send) => latest.current.handlers.attach(selections, send),
      open: (target) => latest.current.handlers.open(target),
      closeDialog: (result) => latest.current.handlers.closeDialog(result),
      confirm: (input) => latest.current.handlers.confirm(input),
      selectItem: (item, section) =>
        section === undefined
          ? latest.current.handlers.selectItem(item)
          : latest.current.handlers.selectItem(item, section),
      refreshPage: () => latest.current.handlers.refreshPage(),
      setSections: (sections) => latest.current.handlers.setSections?.(sections),
      setHeader: (header) => latest.current.handlers.setHeader?.(header),
      navigate: (route, push) => latest.current.handlers.navigate?.(route, push),
      back: () => latest.current.handlers.back?.(),
      openInspector: (route, title) => latest.current.handlers.openInspector?.(route, title),
      closeInspector: () => latest.current.handlers.closeInspector?.(),
    });
    bridge.current = host;
    return () => {
      host.dispose();
      bridge.current = null;
    };
  }, [view]);

  // Push what changed: the bridge's context() reads it through `latest` and `themeRef`.
  const callState = props.call?.state;
  const callMuted = props.call?.muted;
  const callLevel = props.call?.level;
  const callSpoken = props.call?.spoken;
  const callPersona = props.call?.persona;
  const actionSequence = props.action?.sequence;
  const originKey = props.origin ? JSON.stringify(props.origin) : props.origin;
  // biome-ignore lint/correctness/useExhaustiveDependencies(theme): A new theme is pushed to the view (context() reads it through themeRef).
  // biome-ignore lint/correctness/useExhaustiveDependencies(props.item): Pushed to the view when it changes (context() reads it through latest).
  // biome-ignore lint/correctness/useExhaustiveDependencies(props.section): Pushed to the view when it changes (context() reads it through latest).
  // biome-ignore lint/correctness/useExhaustiveDependencies(props.selection): Pushed to the view when it changes (context() reads it through latest).
  // biome-ignore lint/correctness/useExhaustiveDependencies(callState): Pushed to the view when it changes (context() reads it through latest).
  // biome-ignore lint/correctness/useExhaustiveDependencies(callMuted): Pushed to the view when it changes (context() reads it through latest).
  // biome-ignore lint/correctness/useExhaustiveDependencies(callLevel): Pushed to the view when it changes (context() reads it through latest).
  // biome-ignore lint/correctness/useExhaustiveDependencies(callSpoken): Pushed to the view when it changes (context() reads it through latest).
  // biome-ignore lint/correctness/useExhaustiveDependencies(callPersona): Pushed to the view when it changes (context() reads it through latest).
  // biome-ignore lint/correctness/useExhaustiveDependencies(props.layout): Pushed to the view when it changes (context() reads it through latest).
  // biome-ignore lint/correctness/useExhaustiveDependencies(props.route): Pushed to the view when it changes (context() reads it through latest).
  // biome-ignore lint/correctness/useExhaustiveDependencies(originKey): Pushed to the view when it changes (context() reads it through latest).
  // biome-ignore lint/correctness/useExhaustiveDependencies(actionSequence): Pushed to the view when it changes (context() reads it through latest).
  // biome-ignore lint/correctness/useExhaustiveDependencies(props.inspector): Pushed to the view when it changes (context() reads it through latest).
  useEffect(() => {
    bridge.current?.pushContext();
  }, [
    theme,
    props.item,
    props.section,
    props.selection,
    callState,
    callMuted,
    callLevel,
    callSpoken,
    callPersona,
    props.layout,
    props.route,
    originKey,
    actionSequence,
    props.inspector,
  ]);

  if (error !== null)
    return (
      <div className="p-4">
        <InlineMessage title="This view is unavailable." tone="error">
          {error}
        </InlineMessage>
      </div>
    );
  if (view === null) return <div aria-busy="true" className="size-full" ref={setHost} />;
  // Any view may go full screen (a presentation), on a click of the user's.
  const allow = [...(props.media ?? []), 'fullscreen']
    .map((feature) => `${feature} ${view.origin}`)
    .join('; ');
  return (
    <iframe
      allow={allow}
      className="size-full border-0 bg-transparent"
      ref={frameRef}
      // Its own origin isolates it; the sandbox only keeps it from navigating the app.
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals"
      src={props.fragment ? `${view.url}#${props.fragment}` : view.url}
      title={props.title}
    />
  );
}
